import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { api } from "../../utils/fetch.js";
import { z } from "zod";

// GET /public/{slug}/file/{id}/insights

const InsightTypeSchema = z.enum([
  "analyze",
  "execute",
  "review",
  "site_safety",
  "workmanship"
]);

export type PublicInsightType = z.infer<typeof InsightTypeSchema>;

const PublicFileInsightSchema = z.object({
  id: z.number(),
  content: z.string(),
  insight_type: InsightTypeSchema,
  created_at: z.string(),
  created_by: z.string().nullable()
});

export type PublicFileInsight = z.infer<typeof PublicFileInsightSchema>;

const ResponseSchema = z.array(PublicFileInsightSchema);

const RequestSchema = z.object({
  slug: z.string(),
  id: z.string()
});

type RequestType = z.infer<typeof RequestSchema>;

const QUERY_KEY = ["publicFileInsights"] as const;

const fetcher = (params: RequestType) =>
  api<RequestType, PublicFileInsight[]>({
    method: "GET",
    url: `/public/:slug/file/:id/insights`,
    requestSchema: RequestSchema,
    responseSchema: ResponseSchema,
    body: params
  });

type UsePublicFileInsightsOptions = {
  variables: RequestType;
  enabled?: boolean;
};

export function usePublicFileInsights({
  variables,
  enabled = true
}: UsePublicFileInsightsOptions) {
  return useQuery({
    queryKey: [...QUERY_KEY, variables] as const,
    queryFn: () => fetcher(variables),
    enabled
  });
}

export function usePublicFileInsightsSuspense(variables: RequestType) {
  return useSuspenseQuery({
    queryKey: [...QUERY_KEY, variables],
    queryFn: () => fetcher(variables)
  });
}

export const publicFileInsightsQuery = {
  queryKey: QUERY_KEY,
  fetcher
};
